import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {LongId} from 'yougile-util'
import {CompanyLink} from '@client/company-link'
import {LinkButton} from '@components/common/base/buttons'
import {Input} from '@components/common/base/input'
import {globals} from '@extensions/globals-base'
import {isAdmin} from '../dashboard-utils'
import {
  CompanyInviteContext,
  CompanyInviteStore,
  useCompanyInviteStore
} from './company-invite-store'

export const CompanyInvite = () => {
  const [store] = useState(() => new CompanyInviteStore())

  const upDomain = () => store.upDomain()

  useEffect(() => {
    let companyLinkListener: LongId
    void CompanyLink.listener.addListener(upDomain).then(id => {
      companyLinkListener = id
    })
    upDomain()
    CompanyLink.load()
    const listener = globals.onCompanyChange(() => {
      CompanyLink.load()
      upDomain()
    })

    return () => {
      globals.removeCompanyListener(listener)
      void CompanyLink.listener.removeListener(companyLinkListener)
    }
  }, [])

  return (
    <CompanyInviteContext.Provider value={store}>
      <CompanyInviteBase />
    </CompanyInviteContext.Provider>
  )
}

const CompanyInviteBase = observer(() => {
  const [state] = useCompanyInviteStore()

  if (!state.loaded) {
    return null
  }

  return <Form />
})

const Form = () => {
  return (
    <div className="flex flex-col">
      <Editing />
      <CurrentLink />
      <CreateLink />
      <Error />
    </div>
  )
}

const TwWrap = tw.div`
  flex
  justify-between
  items-center
  gap-16
`

const TwInner = tw.div`
  flex
  gap-8
  items-center
`

const TwError = tw.div`
  text-error
  text-10
  mt-8
`

const Error = observer(() => {
  const [state] = useCompanyInviteStore()
  if (!state.error) {
    return null
  }
  return <TwError>{state.error}</TwError>
})

const Editing = observer(() => {
  const [state, store] = useCompanyInviteStore()

  if (!state.editing) {
    return null
  }
  return (
    <TwWrap>
      <TwInner>
        <CompanyInput />
        <div className="text-primary">.yougile.com</div>
      </TwInner>
      <LinkButton onClick={() => store.save()}>{Lang.msg.save}</LinkButton>
    </TwWrap>
  )
})

const CompanyInput = observer(() => {
  const [state, store] = useCompanyInviteStore()
  return (
    <Input
      autoFocus
      value={state.value}
      onChange={({baseElement: {value}}) => store.setValue(value)}
      placeholder="my-company"
      onFocus={({baseElement}) => {
        baseElement?.setSelectionRange(
          baseElement.value.length,
          baseElement.value.length
        )
      }}
      onSubmit={() => store.save()}
      onCancel={() => {
        store.setValue(state.remoteValue)
        store.setEditing(false)
      }}
    />
  )
})

const CurrentLink = observer(() => {
  const [state, store] = useCompanyInviteStore()
  const admin = isAdmin()

  if (!state.value || state.editing) {
    return null
  }
  return (
    <TwWrap>
      <LinkButton
        onClick={() => {
          admin && !state.editing && store.setEditing(true)
        }}
        icon="IconLink"
        className="flex-1"
      >
        {`https://${state.value}.yougile.com`}
      </LinkButton>

      <LinkButton icon="IconCopy" onClick={() => store.copy()}>
        {Lang.msg.permission.regLinkCopy2}
      </LinkButton>
    </TwWrap>
  )
})

const CreateLink = observer(() => {
  const [state, store] = useCompanyInviteStore()
  const admin = isAdmin()

  if (state.value || state.editing) {
    return null
  }
  return (
    <LinkButton
      onClick={() => {
        admin && !state.editing && store.setEditing(true)
      }}
    >
      {Lang.msg.permission.regLinkCreate}
    </LinkButton>
  )
})
